import { DEEP_FIELD_GRAPH_ROLES } from './data';
import type { DeepFieldGraphConfig, DeepFieldGraphRole, DeepFieldGraphStats } from './types';

export const DEFAULT_DEEP_FIELD_GRAPH_CONFIG: DeepFieldGraphConfig = {
    enableDrag: true,
    roles: DEEP_FIELD_GRAPH_ROLES,
    scaleFactor: 0.58,
    seed: 777,
    showLabels: true,
};

export const getDeepFieldGraphStats = (
    roles: readonly DeepFieldGraphRole[] = DEEP_FIELD_GRAPH_ROLES,
): DeepFieldGraphStats => {
    const skills = new Set(roles.flatMap((role) => role.skills));
    const domains = new Set(roles.map((role) => role.domain));
    const skillAssignments = roles.reduce((total, role) => total + role.skills.length, 0);

    return {
        agents: roles.reduce((total, role) => total + role.agents.length, 0),
        averageSkillsPerRole: roles.length === 0 ? 0 : Math.round((skillAssignments / roles.length) * 10) / 10,
        criticalMissingSkills: Math.round(skills.size * 0.08),
        domains: domains.size,
        roles: roles.length,
        skillAssignments,
        skills: skills.size,
    };
};

export const DEEP_FIELD_GRAPH_STATS = getDeepFieldGraphStats();

export interface DeepFieldGraphNode {
    label: string;
    domain: string;
    type: 'role' | 'skill';
    roleIndex?: number;
    groupId: number;
    bx: number;
    by: number;
    bz: number;
    x: number;
    y: number;
    z: number;
    r: number;
    opacity: number;
}

function seededRng(seed: number) {
    let s = seed;
    return () => {
        s++;
        const v = Math.sin(s) * 43758.5453;
        return v - Math.floor(v);
    };
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Ported from the website-copy graph to preserve the exact behavior.
export function buildAllEdges(
    nodes: readonly DeepFieldGraphNode[],
    scale: number,
    roles: readonly DeepFieldGraphRole[],
): [number, number][] {
    const edges: [number, number][] = [];
    const edgeSet = new Set<string>();
    const add = (i: number, j: number) => {
        const key = i < j ? `${i}:${j}` : `${j}:${i}`;
        if (!edgeSet.has(key)) {
            edgeSet.add(key);
            edges.push([i, j]);
        }
    };

    const roleIndices: number[] = [];
    const skillMap = new Map<string, number>();
    const roleMap = new Map(roles.map((role) => [role.label, role]));
    nodes.forEach((n, i) => {
        if (n.type === 'role') roleIndices.push(i);
        else skillMap.set(n.label, i);
    });

    roleIndices.forEach((ri) => {
        const node = nodes[ri];
        if (node === undefined) return;

        const role = roleMap.get(node.label);
        if (!role) return;

        role.skills.forEach((sk) => {
            const si = skillMap.get(sk);
            if (si !== undefined) add(ri, si);
        });
    });

    for (let a = 0; a < roleIndices.length; a++) {
        for (let b = a + 1; b < roleIndices.length; b++) {
            const roleIndexA = roleIndices[a];
            const roleIndexB = roleIndices[b];
            if (roleIndexA === undefined || roleIndexB === undefined) continue;

            const nodeA = nodes[roleIndexA];
            const nodeB = nodes[roleIndexB];
            if (nodeA === undefined || nodeB === undefined) continue;

            const ra = roleMap.get(nodeA.label);
            const rb = roleMap.get(nodeB.label);
            if (ra && rb) {
                const shared = ra.skills.filter((s) => rb.skills.includes(s)).length;
                if (shared >= 1) add(roleIndexA, roleIndexB);
            }
        }
    }

    const maxDist2 = (scale * 0.36) ** 2;
    for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
            const nodeA = nodes[i];
            const nodeB = nodes[j];
            if (nodeA === undefined || nodeB === undefined) continue;

            const dx = nodeA.bx - nodeB.bx;
            const dy = nodeA.by - nodeB.by;
            const dz = nodeA.bz - nodeB.bz;
            if (dx * dx + dy * dy + dz * dz < maxDist2) add(i, j);
        }
    }

    return edges;
}

export function buildJobSkillNodes({
    height,
    roles,
    scale,
    seed,
    width,
}: {
    readonly height: number;
    readonly roles: readonly DeepFieldGraphRole[];
    readonly scale: number;
    readonly seed: number;
    readonly width: number;
}): DeepFieldGraphNode[] {
    const rng = seededRng(seed);
    const nodes: DeepFieldGraphNode[] = [];
    const placedSkills = new Map<string, number>();

    const textLeft = -width * 0.35;
    const textRight = -width * 0.02;
    const textTop = height * 0.15;
    const textBottom = height * 0.5;

    function pushFromText(bx: number, by: number): [number, number] {
        const nx = bx;
        const ny = by;
        if (nx > textLeft && nx < textRight && ny > textTop && ny < textBottom) {
            const pushX = (nx - (textLeft + textRight) / 2) * 1.8;
            const pushY = (ny - (textTop + textBottom) / 2) * 1.8;
            return [nx + pushX, ny + pushY];
        }
        return [nx, ny];
    }

    roles.forEach((role, ri) => {
        const golden = (1 + Math.sqrt(5)) / 2;
        const theta = (2 * Math.PI * ri) / golden;
        const phi = Math.acos(1 - (2 * (ri + 0.5)) / roles.length);
        const dist = scale * (0.5 + rng() * 0.45);
        let rx = dist * Math.sin(phi) * Math.cos(theta);
        let ry = dist * Math.sin(phi) * Math.sin(theta);
        const rz = dist * Math.cos(phi);

        [rx, ry] = pushFromText(rx, ry);

        nodes.push({
            label: role.label,
            domain: role.domain,
            type: 'role',
            roleIndex: ri,
            groupId: ri,
            bx: rx,
            by: ry,
            bz: rz,
            x: 0,
            y: 0,
            z: 0,
            r: 5,
            opacity: 0.9,
        });
        const roleNodeIdx = nodes.length - 1;

        role.skills.forEach((sk, si) => {
            if (placedSkills.has(sk)) return;
            const angle = (si / role.skills.length) * Math.PI * 2 + rng() * 0.4;
            const elev = (rng() - 0.5) * Math.PI * 0.7;
            const orbDist = scale * (0.08 + rng() * 0.12);
            let sx = rx + orbDist * Math.cos(elev) * Math.cos(angle);
            let sy = ry + orbDist * Math.cos(elev) * Math.sin(angle);
            const sz = rz + orbDist * Math.sin(elev);

            [sx, sy] = pushFromText(sx, sy);

            placedSkills.set(sk, nodes.length);
            nodes.push({
                label: sk,
                domain: role.domain,
                type: 'skill',
                roleIndex: roleNodeIdx,
                groupId: ri,
                bx: sx,
                by: sy,
                bz: sz,
                x: 0,
                y: 0,
                z: 0,
                r: 1.8 + rng() * 1.2,
                opacity: 0.4 + rng() * 0.3,
            });
        });
    });

    return nodes;
}
